import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';

// CanvasEditor.jsx cleanup of broken patches

// Override Fabric's text background rendering to add horizontal padding (breathing room)
// and fix the right-side clipping issues for custom fonts.
// Note: We completely overwrite the function without referencing the original to break
// any Hot Module Replacement (HMR) cache chains that were causing 'is not a function' loops!
if (fabric && fabric.Text) {
  // Disable object caching globally for text elements to prevent tight offscreen 
  // bounding boxes from clipping the edges of custom fonts or italicized glyphs.
  fabric.Text.prototype.objectCaching = false;
  
  if (fabric.IText) {
    fabric.IText.prototype.objectCaching = false;
  }

  fabric.Text.prototype._renderTextLinesBackground = function(ctx) {
    if (!this.textBackgroundColor) {
      return; // ImageStudio only uses global textBackgroundColor, safe to skip per-char styles
    }
    
    const padding = this.fontSize * 0.20; // 20% horizontal padding
    const italicPadding = this.fontStyle === 'italic' ? this.fontSize * 0.15 : 0;
    
    let lineTopOffset = this._getTopOffset();
    const leftOffset = this._getLeftOffset();
    
    ctx.fillStyle = this.textBackgroundColor;
    
    for (let i = 0, len = this._textLines.length; i < len; i++) {
      const heightOfLine = this.getHeightOfLine(i);
      
      // Safely get line width regardless of Fabric version
      let lineWidth = 0;
      if (typeof this.getLineWidth === 'function') {
        lineWidth = this.getLineWidth(i);
      } else if (this.__lineWidths && this.__lineWidths[i] !== undefined) {
        lineWidth = this.__lineWidths[i];
      }
      
      const lineLeftOffset = this._getLineLeftOffset(i);
      
      // Draw a single block for the entire line, INCLUDING the padding on both sides
      ctx.fillRect(
        leftOffset + lineLeftOffset - padding,
        lineTopOffset,
        lineWidth + (padding * 2) + italicPadding,
        heightOfLine / this.lineHeight
      );
      
      lineTopOffset += heightOfLine;
    }
  };
}

/**
 * CanvasEditor wraps fabric.js Canvas and handles lifecycle, background loading,
 * event routing, and rendering.
 */
export default function CanvasEditor({
  canvasWidth,
  canvasHeight,
  onCanvasReady,
  onObjectSelected,
  backgroundImage,
}) {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Handle dynamic scaling based on container size
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      // Calculate scale to fit canvas inside container with 32px padding
      const padding = 32;
      const scaleX = (width - padding) / canvasWidth;
      const scaleY = (height - padding) / canvasHeight;
      // Don't scale up beyond 1, just scale down if it doesn't fit
      const newScale = Math.min(scaleX, scaleY, 1);
      setScale(newScale);
      
      if (fabricCanvasRef.current) {
        const canvas = fabricCanvasRef.current;
        // Dynamically resize the fabric canvas DOM element
        canvas.setDimensions({
          width: canvasWidth * newScale,
          height: canvasHeight * newScale
        });
        canvas.setZoom(newScale);
        canvas.calcOffset();
      }
    });
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [canvasWidth, canvasHeight]);

  // Initialize fabric canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: canvasWidth * scale,
      height: canvasHeight * scale,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true, // Selected objects don't jump to top automatically
    });

    canvas.setZoom(scale);

    fabricCanvasRef.current = canvas;
    onCanvasReady(canvas);

    // Selection event handler
    const handleSelection = (e) => {
      const selectedObject = e.selected ? e.selected[0] : null;
      if (selectedObject && (selectedObject.type === 'text' || selectedObject.type === 'i-text')) {
        onObjectSelected(selectedObject);
      } else {
        onObjectSelected(null);
      }
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);
    canvas.on('selection:cleared', () => onObjectSelected(null));

    // Handle delete key
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeObj = canvas.getActiveObject();
        // Don't delete if editing text in IText
        if (activeObj && !activeObj.isEditing) {
          canvas.remove(activeObj);
          canvas.requestRenderAll();
          onObjectSelected(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.dispose();
    };
  }, [canvasWidth, canvasHeight, onCanvasReady]); // Run when canvas dimensions change

  // Force re-measurement of all text elements once custom Google Fonts are fully loaded in the browser
  useEffect(() => {
    let active = true;
    
    if (document.fonts) {
      document.fonts.ready.then(() => {
        if (active && fabricCanvasRef.current) {
          const canvas = fabricCanvasRef.current;
          canvas.forEachObject((obj) => {
            if (obj.type === 'text' || obj.type === 'i-text') {
              if (typeof obj._clearCache === 'function') obj._clearCache();
              obj.initDimensions(); // Recalculate widths using the loaded font metrics
              obj.dirty = true;
            }
          });
          canvas.requestRenderAll();
        }
      });
    }

    return () => {
      active = false;
    };
  }, []);

  // Load background image
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !backgroundImage) return;

    let active = true;

    fabric.Image.fromURL(
      backgroundImage,
      (img, isError) => {
        if (!active) return;
        if (isError || !img) {
          console.error("Fabric failed to load background image:", backgroundImage ? backgroundImage.substring(0, 100) + '...' : null);
          return;
        }

        const scaleX = canvasWidth / img.width;
        const scaleY = canvasHeight / img.height;
        const scaleBg = Math.max(scaleX, scaleY);

        canvas.setBackgroundImage(
          img,
          () => {
            if (active && canvas.lowerCanvasEl && fabricCanvasRef.current) {
              canvas.renderAll();
            }
          },
          {
            scaleX: scaleBg,
            scaleY: scaleBg,
            left: canvasWidth / 2,
            top: canvasHeight / 2,
            originX: 'center',
            originY: 'center',
          }
        );
      },
      { crossOrigin: 'anonymous' }
    );

    return () => {
      active = false;
    };
  }, [backgroundImage, canvasWidth, canvasHeight]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        overflow: 'hidden'
      }}
    >
      <div 
        className="canvas-wrapper" 
        style={{ 
          width: canvasWidth * scale, 
          height: canvasHeight * scale,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          background: 'white'
        }}
      >
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}
